import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FOCUS_LABEL_PARAM,
  FOCUS_PARAM,
  FOCUS_SECTION_PARAM,
  focusKeys,
  focusPath,
  getFocusRequest,
  isSamePath,
  requestFocus,
  resolveSection,
  subscribeFocus,
} from '../utils/recordFocus';

/**
 * Page side of the alert → record deep link. Call once near the top of a page
 * that lists records an alert can point at:
 *
 *   useRecordFocus({ aliases: ['order'], onFocus: () => setActiveTab('orders') });
 *
 * It reads `?focus=` / `?focusLabel=` (plus any legacy id params named in
 * `aliases`, e.g. the `?lot=` and `?order=` links already emitted by the backend),
 * hands the keys to the shared focus store, and strips the params so a refresh or
 * a back-navigation does not flash the row a second time.
 *
 * @param {string[]} aliases  extra query params that also name a record
 * @param {Function} onFocus  called with the keys — switch tabs / clear filters
 *                            here so the record is actually in the rendered list
 */
export function useRecordFocus({ aliases = [], onFocus } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  // Alias list is usually an inline literal — key the effect off its contents,
  // not its identity, so it does not re-run on every render.
  const aliasKey = aliases.join(',');

  useEffect(() => {
    const aliasNames = aliasKey ? aliasKey.split(',') : [];
    const keys = focusKeys(
      searchParams.get(FOCUS_PARAM),
      searchParams.getAll(FOCUS_LABEL_PARAM),
      ...aliasNames.map((name) => searchParams.get(name)),
    );
    // A section on its own is a valid request: the page cannot show the row, but it
    // can still point at the panel the record lives in.
    if (keys.length === 0 && !searchParams.get(FOCUS_SECTION_PARAM)) return;

    const section = searchParams.get(FOCUS_SECTION_PARAM);
    const next = new URLSearchParams(searchParams);
    [FOCUS_PARAM, FOCUS_LABEL_PARAM, FOCUS_SECTION_PARAM, ...aliasNames]
      .forEach((name) => next.delete(name));
    setSearchParams(next, { replace: true });

    onFocusRef.current?.(keys);
    requestFocus(keys, { section });
  }, [searchParams, setSearchParams, aliasKey]);
}

/** Live view of the current focus request — used by the pager to jump to the page
 *  holding the record, and by anything that wants to render its own affordance. */
export function useFocusRequest() {
  const [request, setRequest] = useState(getFocusRequest);
  useEffect(() => subscribeFocus(setRequest), []);
  return request;
}

/**
 * Alert side. Returns `open(target)` where target is
 * `{ path, id, label, section, params }`: it flashes in place when the record is
 * already on this page, and navigates with focus params when it is not. `section` is
 * the fallback — the panel to highlight when that exact record is not on screen.
 */
export function useOpenRecord() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  return (target) => {
    if (!target) return;
    const { path, id, label, section, params } = target;
    const keys = focusKeys(id, label);
    // Same page, but a link that names a tab or a case the page is not showing
    // must still navigate — a hunt alone cannot switch a page's view.
    const wantsQuery = Boolean(path && path.includes('?')) && !search.includes(String(path).split('?')[1]);
    if (!path || (isSamePath(path, pathname) && !wantsQuery)) {
      requestFocus(keys, { section: resolveSection(path || pathname, section) });
      return;
    }
    navigate(focusPath(path, { id, label, section, params }));
  };
}

export default useRecordFocus;
